<template><div><code-well-header>
  <dt-empty-state
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<h2 id="anatomy" tabindex="-1" class="d-docsite--header-2">Anatomy <a class="header-anchor d-link d-docsite--link d-link" href="#anatomy" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g-cols3 d-ba d-bc-subtle d-mb16 d-bar8 d-bgc-secondary">
  <div class="d-gc2 d-bar8 d-bgc-primary"><img class="d-bar8 d-d-block d-w100p" alt="empty state bullets" src="/assets/images/components/empty-state01.png"></div>
  <div class="d-gc1 d-bl d-bc-subtle d-p32">
    <ol class="d-pl16">
      <li class="d-lst-decimal">Illustration or Icon <span class="d-fc-tertiary">(optional)</span></li>
      <li class="d-lst-decimal">Title</li>
      <li class="d-lst-decimal">Description <span class="d-fc-tertiary">(optional)</span></li>
      <li class="d-lst-decimal">
        Action <span class="d-fc-tertiary">(optional)</span>
        <ul class="d-pl16">
          <li class="d-lst-disc">Secondary</li>
          <li class="d-lst-disc">Primary</li>
        </ul>
      </li>
      <li class="d-lst-decimal">Surface <span class="d-fc-tertiary">(optional)</span></li>
    </ol>
  </div>
</div>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Empty states fill the space of its surrounding container and is always  centered vertically and horizontally.</li>
<li class="d-docsite--list-element">Actions are optional if there is no direct action to be taken.</li>
<li class="d-docsite--list-element">The primary action is always to the right when presenting multiple actions.</li>
<li class="d-docsite--list-element">Action buttons are usually <code v-pre>kind=&quot;default&quot;</code> (aka &quot;primary&quot;), though may be <code v-pre>kind=&quot;muted&quot;</code>. Do not mix <code v-pre>kind</code>s.</li>
<li class="d-docsite--list-element">Surface color most often will be transparent, deferring to its parent container's surface color.</li>
</ul>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<h3 id="guidelines" tabindex="-1" class="d-docsite--header-3">Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#guidelines" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Offer clear and actionable guidance so the user has the bare minimum amount of information necessary to proceed.</li>
<li class="d-docsite--list-element">Use scannable, concise, informative, and useful titles that reinforce why this state is empty.</li>
<li class="d-docsite--list-element">Provide a description that complements the title, offering additional context or guidance.</li>
<li class="d-docsite--list-element">Choose visuals that complement the message, its tone, and do not distract from the real task. The content is primarily what should gain their attention.</li>
<li class="d-docsite--list-element">Provide relevant actions that guide the user on how to proceed.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid creating a dead-end. Always provide actionable guidance and/or actions.</li>
<li class="d-docsite--list-element">Don't overload with information or unnecessary details. Keep messaging brief and to the point.</li>
<li class="d-docsite--list-element">Do not use technical jargon or proprietary terms. Keep the language user-friendly and straight-forward.</li>
<li class="d-docsite--list-element">Avoid customizing its style. It should inherit the surrounding container's style when not in an empty state.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="scenarios" tabindex="-1" class="d-docsite--header-3">Scenarios <a class="header-anchor d-link d-docsite--link d-link" href="#scenarios" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The four most likely scenarios for an empty state are <strong>Zero State</strong>, <strong>No Results</strong>, <strong>New Feature</strong>, and <strong>Not Enabled</strong>. This is not an exhaustive list, but a good starting point for most use cases.</p>
<div class="d-d-grid d-g-cols1 lg:d-g-cols3 d-bar8 d-bgc-secondary d-mb32">
  <div class="d-p16 d-p16 d-plc-center">
<h4 class="d-docsite--header-4">Zero State</h4>
<p class="d-docsite--paragraph">The Empty State should guide the user on how to achieve a non-empty state. You might provide a useful tip, such as a shortcut or explanation of what needs to occur if no direct action can be taken.</p>
  </div>
  <div class="d-gc2 d-p16">
    <img class="d-ba d-bc-subtle d-bar4 d-w100p d-bs-sm" alt="Example: Zero state" src="/assets/images/components/empty-state02.png">
  </div>
</div>
<div class="d-d-grid d-g-cols1 lg:d-g-cols3 d-bar8 d-bgc-secondary d-mb32">
  <div class="d-p16 d-p16 d-plc-center">
<h4 class="d-docsite--header-4">No Results</h4>
<p class="d-docsite--paragraph">When an action results in no data or information to display, recommend alternate steps to take. For example, suggest an alternative filter if a table search yields no results, or guide the user on creating a new entry with the desired properties.</p>
  </div>
  <div class="d-gc2 d-p16">
    <img class="d-ba d-bc-subtle d-bar8 d-w100p d-bs-sm" alt="Example: No results" src="/assets/images/components/empty-state03.png">
  </div>
</div>
<div class="d-d-grid d-g-cols1 lg:d-g-cols3 d-bar8 d-bgc-secondary d-mb32">
  <div class="d-p16 d-p16 d-plc-center">
<h4 class="d-docsite--header-4">New Feature</h4>
<p class="d-docsite--paragraph">An opportunity to introduce something new or not yet take advantage of. If it includes a plan change, clearly inform the user. Use this space to entice the user to explore and utilize the new feature.</p>
  </div>
  <div class="d-gc2 d-p16">
    <img class="d-ba d-bc-subtle d-bar8 d-w100p d-bs-sm" alt="Example: New feature" src="/assets/images/components/empty-state04.png">
  </div>
</div>
<div class="d-d-grid d-g-cols1 lg:d-g-cols3 d-bar8 d-bgc-secondary d-mb32">
  <div class="d-p16 d-p16 d-plc-center">
<h4 class="d-docsite--header-4">Not Enabled</h4>
<p class="d-docsite--paragraph">Appropriate for indicating that something is currently unavailable to them. Provide guidance on how they can gain access or why the feature is not enabled.</p>
  </div>
  <div class="d-gc2 d-p16">
    <img class="d-ba d-bc-subtle d-bar8 d-w100p d-bs-sm" alt="Example: Not enabled" src="/assets/images/components/empty-state05.png">
  </div>
</div>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A few rules to keep in nothing-to-see-here when choosing a size variant.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Choosing a size adjusts the relative width, padding, and font size.</li>
<li class="d-docsite--list-element"><code v-pre>lg</code> and <code v-pre>md</code> sizes
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The visual may be an Illustration or an Icon.</li>
<li class="d-docsite--list-element">If both are chosen, the Illustration takes precedence.</li>
</ul>
</li>
<li class="d-docsite--list-element"><code v-pre>sm</code> size
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Illustrations are not available, an Icon is the only visual available.</li>
<li class="d-docsite--list-element">If both are chosen, the Icon takes precedence.</li>
</ul>
</li>
</ul>
<h3 id="large-with-illustration" tabindex="-1" class="d-docsite--header-3">Large with Illustration <a class="header-anchor d-link d-docsite--link d-link" href="#large-with-illustration" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="largeIllustrationExample"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.largeIllustrationExample'
vueCode='
<dt-empty-state
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #illustration>
    <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="large-with-icon" tabindex="-1" class="d-docsite--header-3">Large with Icon <a class="header-anchor d-link d-docsite--link d-link" href="#large-with-icon" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="largeIconExample"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #icon="{ iconSize }">
      <dt-icon-box :size="iconSize"></dt-icon-box>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.largeIconExample'
vueCode='
<dt-empty-state
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #icon="{ iconSize }">
    <dt-icon-box :size="iconSize"></dt-icon-box>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="medium-with-illustration" tabindex="-1" class="d-docsite--header-3">Medium with Illustration <a class="header-anchor d-link d-docsite--link d-link" href="#medium-with-illustration" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="mediumIllustrationExample"
    size="md"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.mediumIllustrationExample'
vueCode='
<dt-empty-state
  size="md"
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #illustration>
    <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="medium-with-icon" tabindex="-1" class="d-docsite--header-3">Medium with Icon <a class="header-anchor d-link d-docsite--link d-link" href="#medium-with-icon" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="mediumIconExample"
    size="md"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #icon="{ iconSize }">
      <dt-icon-box :size="iconSize"></dt-icon-box>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.mediumIconExample'
vueCode='
<dt-empty-state
  size="md"
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #icon="{ iconSize }">
    <dt-icon-box :size="iconSize"></dt-icon-box>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="small" tabindex="-1" class="d-docsite--header-3">Small <a class="header-anchor d-link d-docsite--link d-link" href="#small" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="smallExample"
    size="sm"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #icon="{ iconSize }">
      <dt-icon-box :size="iconSize"></dt-icon-box>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.smallExample'
vueCode='
<dt-empty-state
  size="sm"
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #icon="{ iconSize }">
    <dt-icon-box :size="iconSize"></dt-icon-box>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="no-actions" tabindex="-1" class="d-docsite--header-3">No Actions <a class="header-anchor d-link d-docsite--link d-link" href="#no-actions" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="noActionsExample"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.noActionsExample'
vueCode='
<dt-empty-state
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #illustration>
    <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="no-description" tabindex="-1" class="d-docsite--header-3">No Description <a class="header-anchor d-link d-docsite--link d-link" href="#no-description" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="noDescriptionExample"
    header-text="Nothing to see here"
  >
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.noDescriptionExample'
vueCode='
<dt-empty-state
  header-text="Nothing to see here"
>
  <template #illustration>
    <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="everything" tabindex="-1" class="d-docsite--header-3">Everything <a class="header-anchor d-link d-docsite--link d-link" href="#everything" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="everythingExample"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button importance="clear">Action</dt-button>
        <dt-button>Action</dt-button>
      </dt-stack>
    </template>
    <template #illustration>
      <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.everythingExample'
vueCode='
<dt-empty-state
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button importance="clear">Action</dt-button>
      <dt-button>Action</dt-button>
    </dt-stack>
  </template>
  <template #illustration>
    <dt-illustration-nothing-to-see-here></dt-illustration-nothing-to-see-here>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h3 id="small-with-muted-actions" tabindex="-1" class="d-docsite--header-3">Small, with Muted Actions <a class="header-anchor d-link d-docsite--link d-link" href="#small-with-muted-actions" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-empty-state
    ref="smallMutedActionsExample"
    size="sm"
    header-text="Nothing to see here"
    body-text="Looks like there is no data to display here."
  >
    <template #body>
      <dt-stack direction="row" gap="300">
        <dt-button kind="muted" size="sm" importance="clear">Action</dt-button>
        <dt-button kind="muted" importance="outlined" size="sm">Action</dt-button>
      </dt-stack>
    </template>
    <template #icon="{ iconSize }">
      <dt-icon-box :size="iconSize"></dt-icon-box>
    </template>
  </dt-empty-state>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.smallMutedActionsExample'
vueCode='
<dt-empty-state
  size="sm"
  header-text="Nothing to see here"
  body-text="Looks like there is no data to display here."
>
  <template #body>
    <dt-stack direction="row" gap="300">
      <dt-button kind="muted" size="sm" importance="clear">Action</dt-button>
      <dt-button kind="muted" importance="outlined" size="sm">Action</dt-button>
    </dt-stack>
  </template>
  <template #icon="{ iconSize }">
    <dt-icon-box :size="iconSize"></dt-icon-box>
  </template>
</dt-empty-state>
'
showHtmlWarning />
<h2 id="writing-guidelines" tabindex="-1" class="d-docsite--header-2">Writing Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#writing-guidelines" aria-hidden="true">#</a></h2>
<h3 id="empty-state-parts" tabindex="-1" class="d-docsite--header-3">Empty State Parts <a class="header-anchor d-link d-docsite--link d-link" href="#empty-state-parts" aria-hidden="true">#</a></h3>
<h4 class="d-docsite--header-4">Title</h4>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Should be scannable and informative.</li>
<li class="d-docsite--list-element">Short and concise.</li>
</ul>
<h4 class="d-docsite--header-4">Description</h4>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Short and to the point.</li>
<li class="d-docsite--list-element">Avoid repeating the title's message, favoring complementary and useful information.</li>
<li class="d-docsite--list-element">Inline text links (i.e. <code v-pre>&lt;a class=&quot;d-link&quot;&gt;...&lt;/a&gt;</code>) may be appropriate given its use case, though first consider if it should be an action button.</li>
</ul>
<h4 class="d-docsite--header-4">Actions</h4>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Follow existing writing guidelines for Dialtone's Button commponent.</li>
<li class="d-docsite--list-element">Connect its labels to the paired Title and Description.</li>
</ul>
<h3 id="scenarios-1" tabindex="-1" class="d-docsite--header-3">Scenarios <a class="header-anchor d-link d-docsite--link d-link" href="#scenarios-1" aria-hidden="true">#</a></h3>
<h4 class="d-docsite--header-4">Zero State</h4>
<p class="d-docsite--paragraph">Encourage and guide the user through product engagement.</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">Title: No Ai recaps yet</span>
<span class="line">Description: Enable Ai for calls and meetings and check back here!</span>
<span class="line">Action: [Show me how]</span>
<span class="line"></span></code></pre>
</div><h4 class="d-docsite--header-4">No Results</h4>
<p class="d-docsite--paragraph">Consider the user’s intent and provide guidance to resolve in a useful result.</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">Title: No matching results</span>
<span class="line">Description: Try adjusting your search criteria or filters.</span>
<span class="line"></span></code></pre>
</div><h4 class="d-docsite--header-4">New Feature</h4>
<p class="d-docsite--paragraph">Call out interesting new features or services, and entice the user to explore them.</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">Title: Discover Ai recaps</span>
<span class="line">Description: Enable Ai for calls and meetings to view and share a recap.</span>
<span class="line">Action: [Tell me more]</span>
<span class="line"></span></code></pre>
</div><h4 class="d-docsite--header-4">Not Available</h4>
<p class="d-docsite--paragraph">Be direct in communicating the unavailability of a feature or service, and provide quick actions.</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">Title: Restricted access</span>
<span class="line">Description: Permission required. You can request access from the owner.</span>
<span class="line">Action: [Cancel] [Request access]</span>
<span class="line"></span></code></pre>
</div><h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="empty_state" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">At a minimum, empty state requires a body of content. It can optionally contain a header with buttons, or a footer with buttons/text.</p>
<component-class-table component-name="empty-state"></component-class-table></div></template>


<script setup>
import {
  DtIllustrationNothingToSeeHere,
  DtIconBox,
} from '@dialpad/dialtone-icons/vue3';
</script>